import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface InlineEditCellProps {
  value: string | number | undefined;
  onSave: (newValue: string | number) => void;
  type?: 'text' | 'number';
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  align?: 'left' | 'center' | 'right';
}

export const InlineEditCell = ({
  value,
  onSave,
  type = 'text',
  className,
  disabled = false,
  placeholder = '-',
  align = 'center'
}: InlineEditCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value?.toString() || '');
  }, [value]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue !== value?.toString()) {
      const newValue = type === 'number' ? parseFloat(editValue) || 0 : editValue;
      onSave(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setEditValue(value?.toString() || '');
      setIsEditing(false);
    }
  };

  const alignmentClasses = {
    left: { container: 'justify-start', input: 'text-left' },
    center: { container: 'justify-center', input: 'text-center' },
    right: { container: 'justify-end', input: 'text-right' },
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type={type}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={cn(
          "h-8 text-sm font-mono",
          alignmentClasses[align].input,
          className
        )}
        min={type === 'number' ? 0 : undefined}
      />
    );
  }

  return (
    <div
      onClick={handleClick}
      className={cn(
        "h-8 px-2 flex items-center cursor-pointer rounded border border-transparent hover:border-border hover:bg-muted/50 transition-colors",
        alignmentClasses[align].container,
        disabled && "cursor-default hover:border-transparent hover:bg-transparent",
        className
      )}
    >
      <span className={cn("font-mono text-sm", alignmentClasses[align].input)}>
        {value !== undefined && value !== null && value !== '' ? value : placeholder}
      </span>
    </div>
  );
};
